import { useState } from 'react';

/** Page size for the Collections pane's Saved Searches grid. */
const COLLECTIONS_PAGE_SIZE = 36;

export type ClientPagination<T> = {
  /** The slice of `items` for the current page. */
  pageItems: readonly T[];
  pageNumber: number;
  totalPages: number;
  setPageNumber: (pageNumber: number) => void;
};

/**
 * Slices an already-fetched list into fixed-size pages in the browser. The
 * Collections pane's Saved Searches tab fetches one capped list in a single
 * pass, so its paging happens here rather than per request. The current page is
 * clamped back into range when the list shrinks (e.g. after a delete).
 *
 * The Favourites tab does not use this: it pages against the saved-listings
 * endpoint instead, a request per page (see `use-collections-favourites`).
 */
export function useClientPagination<T>(
  items: readonly T[],
  pageSize: number = COLLECTIONS_PAGE_SIZE
): ClientPagination<T> {
  const [requestedPage, setPageNumber] = useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  // Clamp during render (rather than via an effect) so a shrunk list — e.g. after
  // a delete — never leaves us slicing past the end and showing an empty page.
  const pageNumber = Math.min(requestedPage, totalPages);

  const start = (pageNumber - 1) * pageSize;
  const pageItems = items.slice(start, start + pageSize);

  return { pageItems, pageNumber, totalPages, setPageNumber };
}
